import {getTimeZones} from '@vvo/tzdb';
import {
  format,
  formatDistanceToNowStrict,
  isToday,
  isYesterday,
  isTomorrow,
  differenceInDays,
  addBusinessDays,
} from 'date-fns';

/** Clock-style duration: `m:ss`, or `h:mm:ss` once an hour has passed. */
export function formatDuration(ms: number): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1_000));
  const hours = Math.floor(totalSeconds / 3_600);
  const minutes = Math.floor((totalSeconds % 3_600) / 60);
  const seconds = totalSeconds % 60;
  const pad = (value: number) => String(value).padStart(2, '0');

  return hours > 0
    ? `${hours}:${pad(minutes)}:${pad(seconds)}`
    : `${minutes}:${pad(seconds)}`;
}

export function parseISO8601DateString(dateString: string): Date {
  const asDate = new Date(`${dateString}T00:00:00`);
  return new Date(asDate.getTime() + asDate.getTimezoneOffset() * 60 * 1000);
}

interface FormatDateOptions {
  style?:
    | 'long'
    | 'medium'
    | 'short'
    | 'time'
    | 'duration'
    | 'relative'
    | 'conversational'
    | 'conversationalShort'
    | 'message';
  includeTime?: boolean;
}

export function generatePrefilledMessageSchedulingOptions(): Date[] {
  const date = new Date();

  const first = addBusinessDays(date, 1);
  first.setHours(9, 0, 0, 0);

  const second = addBusinessDays(date, 2);
  second.setHours(9, 0, 0, 0);

  return [first, second];
}

export function getHumanReadableTimeZone() {
  const zones = getTimeZones({includeUtc: true});
  const name = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const offset = format(new Date(), 'xxx');

  const timeZone = zones.find((zone) => zone.name === name);

  if (!timeZone) {
    return `${name} (UTC${offset})`;
  }

  const {mainCities, countryName} = timeZone;
  const [primaryCity] = mainCities;

  return `${primaryCity}, ${countryName} (UTC${offset})`;
}

/**
 * Format a date with various style options
 * @param date The date to format
 * @param options Formatting options
 * @param options.style 'long' (May 16, 2025), 'medium' (Jan 16, 2025), 'short' (May 16), 'time' (4:30 PM), or 'relative' (X ago)
 * @param options.includeTime Whether to include the time in the formatted date
 * @returns Formatted date string
 */
export function formatDate(
  date: string | Date,
  options?: FormatDateOptions,
): string {
  const {style = 'medium', includeTime = false} = options || {};

  // Convert string dates to Date objects
  let dateObj: Date;

  if (typeof date === 'string') {
    // Check if the string date has a time component
    const hasTimeComponent = date.includes('T') || date.includes(' ');

    if (!hasTimeComponent) {
      // For date strings without time component, follow the pattern from fields/resolvers.ts
      // Append T00:00:00 and adjust for timezone
      const asDate = new Date(`${date}T00:00:00`);
      dateObj = new Date(
        asDate.getTime() + asDate.getTimezoneOffset() * 60 * 1000,
      );
    } else {
      // Regular date string with time component
      dateObj = new Date(date);
    }
  } else {
    dateObj = date;
  }
  const diffInSeconds = (new Date().getTime() - dateObj.getTime()) / 1000;
  const diffInHours = diffInSeconds / 60 / 60;

  // Format based on style
  switch (style) {
    case 'long':
      return includeTime
        ? format(dateObj, 'MMMM d, yyyy h:mm a')
        : format(dateObj, 'MMMM d, yyyy');

    case 'medium':
      return includeTime
        ? format(dateObj, 'MMM d, yyyy h:mm a')
        : format(dateObj, 'MMM d, yyyy');

    case 'short':
      return format(dateObj, 'MMMM d');

    case 'time':
      return format(dateObj, 'h:mm a');

    case 'duration':
      if (diffInSeconds >= 0 && diffInSeconds < 60) {
        return 'just now';
      }
      return formatDistanceToNowStrict(dateObj);

    case 'relative':
      if (diffInSeconds >= 0 && diffInSeconds < 60) {
        return 'just now';
      }

      return formatDistanceToNowStrict(dateObj, {
        addSuffix: true,
      });

    case 'conversational':
      if (isToday(dateObj)) {
        if (includeTime) {
          return format(dateObj, 'h:mmaaa').toLowerCase();
        } else {
          return 'Today';
        }
      }
      if (isYesterday(dateObj)) {
        return 'Yesterday';
      }
      if (isTomorrow(dateObj)) {
        return 'Tomorrow';
      }

      if (Math.abs(differenceInDays(new Date(), dateObj)) < 6) {
        return format(dateObj, 'EEEE');
      }
      return format(dateObj, 'MM/dd/yy');

    case 'conversationalShort':
      if (isToday(dateObj)) {
        return 'Today';
      }
      if (isYesterday(dateObj)) {
        return 'Yesterday';
      }
      if (isTomorrow(dateObj)) {
        return 'Tomorrow';
      }
      if (Math.abs(differenceInDays(new Date(), dateObj)) < 6) {
        return format(dateObj, 'EEEE');
      }
      return format(dateObj, 'MMM d');

    case 'message':
      if (diffInHours < 24) {
        return format(dateObj, 'h:mm a');
      } else {
        return format(dateObj, 'EEE h:mm a');
      }

    default:
      return includeTime
        ? format(dateObj, 'MMMM d, yyyy h:mm a')
        : format(dateObj, 'MMMM d, yyyy');
  }
}
